import { makeAutoObservable } from "mobx";

import { TranslatorProviderType } from "../../@api";

import { IGlossaryFileSupportExtended } from "../features/translation/TranslationHeader/GlossarySelection/GlossarySelection.types";
import { ILanguageOption, IProgressedFileResult, IUploadFile } from "../models/interfaces";

interface ITextTranslationState {
  textToTranslate: string;
  textTranslationResult: IProgressedFileResult | undefined;
  isTextTranslationLoading: boolean;
  selectedTranslationMethod: TranslatorProviderType;
}

export default class TranslationStore {
  private _textTranslationState: ITextTranslationState = {
    textToTranslate: "",
    isTextTranslationLoading: false,
    textTranslationResult: undefined,
    selectedTranslationMethod: TranslatorProviderType.AzureTranslator,
  };

  private _uploadedFiles: IUploadFile[] = [];
  private _currentProgressingFileIds: string[] = [];
  private _selectedGlossary: IGlossaryFileSupportExtended | null = null;
  private _sourceLanguage: ILanguageOption | null = null;
  private _targetLanguage: ILanguageOption | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // Glossary and language selection
  public get selectedGlossary(): IGlossaryFileSupportExtended | null {
    return this._selectedGlossary;
  }

  public setSelectedGlossary(value: IGlossaryFileSupportExtended | null): void {
    this._selectedGlossary = value;
  }

  public get sourceLanguage(): ILanguageOption | null {
    return this._sourceLanguage;
  }

  public setSourceLanguage(value: ILanguageOption): void {
    this._sourceLanguage = value;
  }

  public get targetLanguage(): ILanguageOption | null {
    return this._targetLanguage;
  }

  public setTargetLanguage(value: ILanguageOption): void {
    this._targetLanguage = value;
  }

  // Text translation state
  public get textTranslationState(): ITextTranslationState {
    return this._textTranslationState;
  }

  public setTextToTranslate(value: string) {
    this._textTranslationState.textToTranslate = value;
  }

  public setTextTranslationResult(value: IProgressedFileResult | undefined) {
    this._textTranslationState.textTranslationResult = value;
  }

  public setIsTextTranslationLoading(value: boolean) {
    this._textTranslationState.isTextTranslationLoading = value;
  }

  public setSelectedTranslationMethod(value: TranslatorProviderType) {
    this._textTranslationState.selectedTranslationMethod = value;
  }

  // File translation state
  public get uploadedFiles(): IUploadFile[] {
    return this._uploadedFiles;
  }

  public setUploadedFiles(value: IUploadFile[]) {
    this._uploadedFiles = value;
  }

  public get isAnyFileProgressing(): boolean {
    return this._currentProgressingFileIds.length > 0;
  }

  public addProgressingFile(fileId: string): void {
    if (this.containsProgressingFile(fileId)) {
      return;
    }

    this._currentProgressingFileIds = [...this._currentProgressingFileIds, fileId];
  }

  public removeProgressingFile(fileId: string): void {
    this._currentProgressingFileIds = this._currentProgressingFileIds.filter(
      progressingFileId => progressingFileId !== fileId,
    );
  }

  public containsProgressingFile(fileId: string): boolean {
    return this._currentProgressingFileIds.some(progressingFileId => progressingFileId === fileId);
  }
}
